import { ANALYTICS } from './analyticsTokens';

export type BetaFeatureId = 'progress_reports' | 'benchmark_monitoring' | 'progress_monitoring' | 'usage_data';

export type StageTone = 'green' | 'orange' | 'grey';

export type MetricKind = 'people' | 'total';

export type DetailRow = { label: string; metric: string; kind: MetricKind; unit: string };

export type DetailGroup = { label: string | null; rows: readonly DetailRow[] };

export type TableColumn = { metric: string; label: string };

export type BetaFeatureSpec = {
  id: BetaFeatureId;
  name: string;
  short: string;
  stage: string;
  stageTone: StageTone;
  headline: string;
  color: string;
  keyAction1: string;
  keyAction2: string;
  metrics: readonly string[];
  columns: readonly [TableColumn, TableColumn];
  groups: readonly DetailGroup[];
};

export const REPORTS_PRINTED = 'progress_report.reports_printed';

const people = (label: string, metric: string): DetailRow => ({ label, metric, kind: 'people', unit: 'people' });

function drillDown(prefix: string): DetailRow[] {
  return [
    people('Opened the dashboard widget', `${prefix}.widget_opened`),
    people('Then opened a school', `${prefix}.school_opened`),
    people('Then opened a teacher', `${prefix}.teacher_opened`),
  ];
}

function monitoringColumns(prefix: string): [TableColumn, TableColumn] {
  return [
    { metric: `${prefix}.widget_opened`, label: 'Opened widget' },
    { metric: `${prefix}.teacher_opened`, label: 'Opened teacher' },
  ];
}

export const BETA_FEATURES: readonly BetaFeatureSpec[] = [
  {
    id: 'progress_reports',
    name: 'Progress reports',
    short: 'Progress reports',
    stage: 'Released',
    stageTone: 'green',
    headline: 'printed a report',
    color: ANALYTICS.indigo700,
    keyAction1: 'progress_report.viewed',
    keyAction2: 'progress_report.printed',
    metrics: ['progress_report.viewed', 'progress_report.printed', REPORTS_PRINTED],
    columns: [
      { metric: 'progress_report.viewed', label: 'Viewed' },
      { metric: REPORTS_PRINTED, label: 'Printed' },
    ],
    groups: [
      {
        label: null,
        rows: [
          people('Viewed at least one report', 'progress_report.viewed'),
          { label: 'Reports viewed', metric: 'progress_report.viewed', kind: 'total', unit: 'reports' },
          people('Printed at least one report', 'progress_report.printed'),
          { label: 'Reports printed', metric: REPORTS_PRINTED, kind: 'total', unit: 'reports' },
        ],
      },
    ],
  },
  {
    id: 'benchmark_monitoring',
    name: 'Benchmark monitoring',
    short: 'Benchmark',
    stage: 'Released',
    stageTone: 'green',
    headline: 'opened a teacher',
    color: ANALYTICS.orange500,
    keyAction1: 'benchmark_monitoring.widget_opened',
    keyAction2: 'benchmark_monitoring.teacher_opened',
    metrics: ['benchmark_monitoring.widget_opened', 'benchmark_monitoring.school_opened', 'benchmark_monitoring.teacher_opened'],
    columns: monitoringColumns('benchmark_monitoring'),
    groups: [{ label: null, rows: drillDown('benchmark_monitoring') }],
  },
  {
    id: 'progress_monitoring',
    name: 'Assignment monitoring',
    short: 'Assignment monitoring',
    stage: 'Released',
    stageTone: 'green',
    headline: 'opened a teacher',
    color: ANALYTICS.teal600,
    keyAction1: 'progress_monitoring.widget_opened',
    keyAction2: 'progress_monitoring.teacher_opened',
    metrics: [
      'progress_monitoring.widget_opened',
      'progress_monitoring.school_opened',
      'progress_monitoring.teacher_opened',
      'progress_monitoring.requirement_changed',
      'progress_monitoring.turned_off',
    ],
    columns: monitoringColumns('progress_monitoring'),
    groups: [
      { label: 'Drill-down', rows: drillDown('progress_monitoring') },
      {
        label: 'Settings',
        rows: [
          people('Changed the default requirement', 'progress_monitoring.requirement_changed'),
          people('Turned assignment monitoring off', 'progress_monitoring.turned_off'),
        ],
      },
    ],
  },
  {
    id: 'usage_data',
    name: 'Usage data',
    short: 'Usage data',
    stage: 'In design',
    stageTone: 'grey',
    headline: 'opened a teacher',
    color: ANALYTICS.pink500,
    keyAction1: 'usage_data.widget_opened',
    keyAction2: 'usage_data.teacher_opened',
    metrics: ['usage_data.widget_opened', 'usage_data.teacher_opened'],
    columns: monitoringColumns('usage_data'),
    groups: [
      {
        label: null,
        rows: [people('Opened the dashboard widget', 'usage_data.widget_opened'), people('Drilled down to a teacher', 'usage_data.teacher_opened')],
      },
    ],
  },
];

export function betaFeatureById(id: string): BetaFeatureSpec | undefined {
  return BETA_FEATURES.find((feature) => feature.id === id);
}
